import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Dashboard
export const getDashboardStats = async () => {
  const response = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() });
  return response.data;
};

// Clients
export const getClients = async (search = '') => {
  const params = search ? { search } : {};
  const response = await axios.get(`${API}/clients`, { 
    headers: getAuthHeaders(),
    params 
  });
  return response.data;
};

export const getClient = async (clientId) => {
  const response = await axios.get(`${API}/clients/${clientId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const createClient = async (clientData) => {
  const response = await axios.post(`${API}/clients`, clientData, { headers: getAuthHeaders() });
  return response.data;
};

// Reviews
export const getClientReviews = async (clientId) => {
  const response = await axios.get(`${API}/reviews/client/${clientId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const getRecentReviews = async (limit = 10) => {
  const response = await axios.get(`${API}/reviews/recent`, { 
    headers: getAuthHeaders(),
    params: { limit }
  });
  return response.data;
};

export const getMyReviews = async () => {
  const response = await axios.get(`${API}/reviews/my`, { headers: getAuthHeaders() });
  return response.data;
};

export const createReview = async (reviewData) => {
  const response = await axios.post(`${API}/reviews`, reviewData, { headers: getAuthHeaders() });
  return response.data;
};

// Subscription
export const createCheckoutSession = async (planType = 'solo') => {
  const response = await axios.post(
    `${API}/subscription/checkout`, 
    { origin_url: window.location.origin, plan_type: planType },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const getCheckoutStatus = async (sessionId) => {
  const response = await axios.get(`${API}/subscription/status/${sessionId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const cancelSubscription = async () => {
  const response = await axios.post(`${API}/subscription/cancel`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Admin
export const getReportedReviews = async () => {
  const response = await axios.get(`${API}/admin/reported-reviews`, { headers: getAuthHeaders() });
  return response.data;
};

export const dismissReport = async (reviewId) => {
  const response = await axios.post(`${API}/admin/reviews/${reviewId}/dismiss`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const deleteReview = async (reviewId) => {
  const response = await axios.delete(`${API}/admin/reviews/${reviewId}`, { headers: getAuthHeaders() });
  return response.data;
};

export const warnUser = async (userId) => {
  const response = await axios.post(`${API}/admin/users/${userId}/warn`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const banUser = async (userId) => {
  const response = await axios.post(`${API}/admin/users/${userId}/ban`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const unbanUser = async (userId) => {
  const response = await axios.post(`${API}/admin/users/${userId}/unban`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Credential Verification
export const getPendingCredentials = async () => {
  const response = await axios.get(`${API}/admin/pending-credentials`, { headers: getAuthHeaders() });
  return response.data;
};

export const verifyUserCredentials = async (userId) => {
  const response = await axios.post(`${API}/admin/users/${userId}/verify`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const rejectUserCredentials = async (userId, reason = 'Invalid or unclear credentials') => {
  const response = await axios.post(`${API}/admin/users/${userId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Notifications
export const getNotifications = async (limit = 20) => {
  const response = await axios.get(`${API}/notifications`, { 
    headers: getAuthHeaders(),
    params: { limit }
  });
  return response.data;
};

export const getUnreadNotificationCount = async () => {
  const response = await axios.get(`${API}/notifications/unread/count`, { headers: getAuthHeaders() });
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await axios.put(`${API}/notifications/${notificationId}/read`, {}, { headers: getAuthHeaders() });
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Referrals
export const getReferralStats = async () => {
  const response = await axios.get(`${API}/referrals/stats`, { headers: getAuthHeaders() });
  return response.data;
};

export const redeemReferralCredit = async () => {
  const response = await axios.post(`${API}/referrals/redeem-credit`, {}, { headers: getAuthHeaders() });
  return response.data;
};

// Reports
export const reportReview = async (reviewId, reason, details = '') => {
  const response = await axios.post(`${API}/reports`, { 
    review_id: reviewId, 
    reason, 
    details 
  }, { headers: getAuthHeaders() });
  return response.data;
};

// Delete own review
export const deleteMyReview = async (reviewId) => {
  const response = await axios.delete(`${API}/reviews/${reviewId}`, { headers: getAuthHeaders() });
  return response.data;
};

// Edit own review
export const updateMyReview = async (reviewId, reviewData) => {
  const response = await axios.put(`${API}/reviews/${reviewId}`, reviewData, { headers: getAuthHeaders() });
  return response.data;
};

// Email Testing (Admin only)
export const testEmailConfig = async () => {
  const response = await axios.get(`${API}/admin/email/test`, { headers: getAuthHeaders() });
  return response.data;
};

export const sendTestEmail = async () => {
  const response = await axios.post(`${API}/admin/email/send-test`, {}, { headers: getAuthHeaders() });
  return response.data;
};
